/**
 * Description: Capabilities listing page with sorting, pagination, filtering, and CRUD operations
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Pencil, Trash2, Layers, ArrowUpDown, ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import { api, Capability } from '@services/api'
import { toast } from 'react-toastify'

type SortField = 'capability_name' | 'capability_code' | 'category' | 'status' | 'updated_at'
type SortDirection = 'asc' | 'desc'

const Capabilities: React.FC = () => {
  const navigate = useNavigate()
  const [capabilities, setCapabilities] = useState<Capability[]>([])
  const [filteredCapabilities, setFilteredCapabilities] = useState<Capability[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'deprecated'>('all')
  const [systemCounts, setSystemCounts] = useState<Record<number, number>>({})

  const [sortField, setSortField] = useState<SortField>(() => {
    const saved = localStorage.getItem('capabilities_sortField')
    return (saved as SortField) || 'capability_name'
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    const saved = localStorage.getItem('capabilities_sortDirection')
    return (saved as SortDirection) || 'asc'
  })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  useEffect(() => {
    loadCapabilities()
  }, [])

  useEffect(() => {
    localStorage.setItem('capabilities_sortField', sortField)
    localStorage.setItem('capabilities_sortDirection', sortDirection)
  }, [sortField, sortDirection])

  useEffect(() => {
    filterCapabilities()
  }, [capabilities, searchTerm, statusFilter])

  const loadCapabilities = async () => {
    try {
      setIsLoading(true)
      const data = await api.getAllCapabilities()
      setCapabilities(data)

      const counts: Record<number, number> = {}
      await Promise.all(
        data.map(async (capability) => {
          if (capability.id) {
            try {
              const systems = await api.getCapabilitySystems(capability.id)
              counts[capability.id] = systems.length
            } catch {
              counts[capability.id] = 0
            }
          }
        }),
      )
      setSystemCounts(counts)
    } catch (error) {
      toast.error('Failed to load capabilities')
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const filterCapabilities = () => {
    let filtered = capabilities

    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c.capability_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.capability_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.category?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter)
    }

    setFilteredCapabilities(filtered)
    setCurrentPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedCapabilities = [...filteredCapabilities].sort((a, b) => {
    let aValue = a[sortField]
    let bValue = b[sortField]

    if (aValue === null || aValue === undefined) aValue = ''
    if (bValue === null || bValue === undefined) bValue = ''

    if (typeof aValue === 'string') aValue = aValue.toLowerCase()
    if (typeof bValue === 'string') bValue = bValue.toLowerCase()

    if (sortField === 'updated_at') {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const totalPages = Math.ceil(sortedCapabilities.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCapabilities = sortedCapabilities.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number, capabilityName: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (window.confirm(`Are you sure you want to delete "${capabilityName}"?`)) {
      try {
        await api.deleteCapability(id)
        toast.success(`${capabilityName} deleted successfully`)
        loadCapabilities()
      } catch (error) {
        toast.error(`Failed to delete ${capabilityName}`)
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }
  }

  const getStatusBadgeColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
      case 'inactive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      case 'deprecated':
        return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
      default:
        return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const SortableHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      className="cursor-pointer px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <ArrowUpDown
          className={`h-4 w-4 ${sortField === field ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'}`}
        />
      </div>
    </th>
  )

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading capabilities...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Capabilities Catalog</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {filteredCapabilities.length} {filteredCapabilities.length === 1 ? 'capability' : 'capabilities'} found
            {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
          </p>
        </div>
        <button
          onClick={() => navigate('/capabilities/create')}
          className="btn-primary flex cursor-pointer items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Create Capability</span>
        </button>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
              placeholder="Search capabilities by name, code, category, description..."
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="deprecated">Deprecated</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {filteredCapabilities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Layers className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No capabilities found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating a new capability'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/capabilities/create')}
                  className="btn-primary inline-flex items-center"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Create Capability
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <SortableHeader field="capability_name" label="Capability Name" />
                    <SortableHeader field="capability_code" label="Code" />
                    <SortableHeader field="category" label="Category" />
                    <SortableHeader field="status" label="Status" />
                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                      System Count
                    </th>
                    <SortableHeader field="updated_at" label="Last Updated" />
                    <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {paginatedCapabilities.map((capability) => (
                    <tr key={capability.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {capability.capability_name}
                          </div>
                          {capability.description && (
                            <div className="max-w-xs truncate text-sm text-gray-500 dark:text-gray-400">
                              {capability.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {capability.capability_code || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {capability.category || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {capability.status && (
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs leading-5 font-semibold ${getStatusBadgeColor(capability.status)}`}
                          >
                            {capability.status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs leading-5 font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {capability.id ? systemCounts[capability.id] || 0 : 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(capability.updated_at)}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                        <div className="flex items-center justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/capabilities/${capability.id}`)}
                            className="text-primary-600 hover:text-primary-900 cursor-pointer rounded p-2 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/capabilities/${capability.id}/edit`)}
                            className="cursor-pointer rounded p-2 text-blue-600 transition-colors hover:text-blue-900 dark:hover:text-blue-300"
                            title="Edit Capability"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {capability.id && (
                            <button
                              onClick={(e) => {
                                if (capability.id) handleDelete(capability.id, capability.capability_name, e)
                              }}
                              className="cursor-pointer rounded p-2 text-red-600 transition-colors hover:text-red-900 dark:hover:text-red-300"
                              title="Delete Capability"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(endIndex, sortedCapabilities.length)}</span> of{' '}
                      <span className="font-medium">{sortedCapabilities.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm dark:shadow-none"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        const showPage =
                          page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)

                        if (showPage) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex cursor-pointer items-center border px-4 py-2 text-sm font-medium ${
                                currentPage === page
                                  ? 'bg-primary-50 border-primary-500 text-primary-600 z-10'
                                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        } else if (
                          (page === currentPage - 2 && page > 1) ||
                          (page === currentPage + 2 && page < totalPages)
                        ) {
                          return (
                            <span
                              key={page}
                              className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                            >
                              ...
                            </span>
                          )
                        }
                        return null
                      })}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex cursor-pointer items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Capabilities
